import {
    Grid2,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Link } from 'react-router-dom';
import SkeletonTable from '../../../components/atom/SkeletonTable';
import { MODAL_TYPE, useModals } from '../../../hooks/useModal';
import { deleteCommissionGroup } from '@openapi/commission-groups/commission-groups';
import useNotify from '../../../hooks/useNotify';
import { TABLE_ROW_COUNT_OPTIONS } from '../../../helpers/constants';
import { handleChangeRowsPerPage, handlePageChange } from '../../../helpers/handleTableFunctions';
import { useTranslation } from 'react-i18next';
import { useCapabilities } from '../../../hooks/useCapabilities';

const CommissionGroupsTable = ({
    rows,
    limit,
    page,
    setParams,
    total,
    loading,
    reloadData,
    showId,
}) => {
    const { t } = useTranslation();
    const { can, CAPABILITIES } = useCapabilities();
    const canManage = can(CAPABILITIES.COMMISSION_GROUPS_MANAGE);
    const { showModal } = useModals();
    const { notifyError, notifySuccess } = useNotify();
    const [deletingId, setDeletingId] = useState(null);

    const handleDeleteCommissionGroup = useCallback(
        async (id) => {
            setDeletingId(id);
            try {
                await deleteCommissionGroup(id);
                notifySuccess(t('commissionGroups.notifications.deleted'));
                reloadData();
            } catch (_error) {
                notifyError(t('commissionGroups.notifications.deleteError'));
            } finally {
                setDeletingId(null);
            }
        },
        [notifyError, notifySuccess, reloadData, t],
    );

    const confirmDelete = useCallback(
        (group) => {
            showModal(MODAL_TYPE.CONFIRMATION, {
                title: t('confirmationModal.title'),
                content: t('confirmationModal.content'),
                confirm: () => handleDeleteCommissionGroup(group.id),
            });
        },
        [handleDeleteCommissionGroup, showModal, t],
    );

    useEffect(() => {
        if (rows?.length === 0 && page > 0) {
            setParams({ limit, page: page - 1 });
        }
    }, [rows, limit, page, setParams]);

    return (
        <Grid2 size={{ xs: 12 }}>
            <TableContainer>
                <Table sx={{ minWidth: 560 }}>
                    <TableHead>
                    <TableRow>
                        <TableCell>{t('commissionGroups.columns.name')}</TableCell>
                        <TableCell>{t('commissionGroups.columns.billingType')}</TableCell>
                        <TableCell>{t('commissionGroups.columns.affiliateContactEmail')}</TableCell>
                        {canManage && <TableCell />}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {loading ? (
                        <SkeletonTable rows={limit} cells={4} />
                    ) : (
                        rows?.map((row) => (
                            <TableRow key={row.id}>
                                <TableCell>{row.name}</TableCell>
                                <TableCell>
                                    {row.billingType
                                        ? t(
                                              `commissionGroups.billingType.options.${row.billingType}`,
                                          )
                                        : '-'}
                                </TableCell>
                                <TableCell>{row.affiliateContactEmail || '-'}</TableCell>
                                {canManage && (
                                    <TableCell align="right">
                                        <Link
                                            to={`/shows/${showId}/commissiongroups/edit/${row.id}`}
                                        >
                                            <Tooltip title={t('common.edit')}>
                                                <IconButton>
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Link>
                                        <Tooltip title={t('common.delete')}>
                                            <IconButton
                                                onClick={() => confirmDelete(row)}
                                                disabled={deletingId === row.id}
                                            >
                                                <DeleteIcon color="error" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))
                    )}
                </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                count={total || 0}
                rowsPerPage={limit}
                component="div"
                page={rows?.length <= 0 ? 0 : page}
                onPageChange={(_, newPage) => handlePageChange(newPage, setParams)}
                rowsPerPageOptions={TABLE_ROW_COUNT_OPTIONS}
                onRowsPerPageChange={(event) => handleChangeRowsPerPage(event, setParams)}
                labelRowsPerPage={t('labelRowsPerPage')}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            />
        </Grid2>
    );
};

export default CommissionGroupsTable;
